import axios from 'axios';
import AuthClientStore from '../../../../common/services/auth-client.service';
import type {
  LoadSpaceResponse,
  MaskResponse,
  MappingResult,
  ApiResponse,
} from '../types';

// API base URL - can be configured via environment variable
// Production: https://py-visualizer.kindsmoke-7d2038ac.francecentral.azurecontainerapps.io/space
// Local dev: http://localhost:9000/space
const API_URL = process.env.REACT_APP_SPACE_MANAGER_API_URL || 'https://py-visualizer.kindsmoke-7d2038ac.francecentral.azurecontainerapps.io/space';

const api = axios.create({
  baseURL: API_URL,
  timeout: 300000, // 5 minutes for long operations
});

// py-visualizer's /space API is super-admin only: it checks this token with the
// PMS API's super-admin guard and refuses requests without it.
api.interceptors.request.use((config) => {
  const token = AuthClientStore.getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Space operations
/** Throw away unpublished edits and reload the published space (same shape as loadSpace). */
export async function discardDraft(spaceId: string): Promise<LoadSpaceResponse> {
  const formData = new FormData();
  formData.append('space_id', spaceId);
  const response = await api.post('/discard_draft', formData);
  return response.data;
}

export async function loadSpace(spaceId: string): Promise<LoadSpaceResponse> {
  const response = await api.post('/load_space', { space_id: spaceId });
  return response.data;
}

export async function getOriginalImage(): Promise<Blob> {
  const response = await api.get('/get_original_image', { responseType: 'blob' });
  return response.data;
}

export async function getReferenceImage(): Promise<Blob> {
  const response = await api.get('/get_reference_image', { responseType: 'blob' });
  return response.data;
}

// Surface operations
export async function getAvailableSurfaces(spaceId?: string): Promise<{ surfaces: string[] }> {
  const response = await api.get('/available_surfaces', {
    params: spaceId ? { space_id: spaceId } : {},
  });
  return response.data;
}

export async function getMask(surface: string, index: number, spaceId?: string): Promise<{ status: string; message: string; output?: string }> {
  const response = await api.get('/get_mask', {
    params: { surface, index_string: index.toString(), ...(spaceId ? { space_id: spaceId } : {}) },
  });
  return response.data;
}

export async function getMaskWithMarker(surface: string, index: number, spaceId?: string): Promise<MaskResponse> {
  const response = await api.get('/get_mask_with_marker', {
    params: { surface, index_string: index.toString(), ...(spaceId ? { space_id: spaceId } : {}) },
  });
  return response.data;
}

export async function changeMask(
  surface: string,
  index: number,
  maskBase64: string,
  recompute = true,
  spaceId?: string
): Promise<ApiResponse> {
  const formData = new FormData();
  formData.append('input_image', maskBase64);
  if (spaceId) formData.append('space_id', spaceId);

  const response = await api.post('/edit_mask', formData, {
    params: {
      surface,
      index_string: index.toString(),
      recompute_mask: recompute ? 'True' : 'False',
    },
  });
  return response.data;
}

export async function updateSurfaceMarker(
  surfaceType: string,
  index: number,
  centroidX: number,
  centroidY: number,
  spaceId?: string
): Promise<ApiResponse> {
  const formData = new FormData();
  formData.append('surface_type', surfaceType);
  formData.append('index', index.toString());
  formData.append('centroid_x', centroidX.toString());
  formData.append('centroid_y', centroidY.toString());
  if (spaceId) formData.append('space_id', spaceId);

  const response = await api.post('/update_surface_marker', formData);
  return response.data;
}

export async function deleteSurface(surface: string, index: number, spaceId?: string): Promise<ApiResponse> {
  const response = await api.get('/delete_surface', {
    params: { surface, index_string: index.toString(), ...(spaceId ? { space_id: spaceId } : {}) },
  });
  return response.data;
}

/**
 * Add one surface from its mask. For a single stairs piece (surface "stairs"),
 * isWall says whether it is a riser (vertical) or a tread (horizontal).
 */
export async function addSurface(surface: string, maskBase64: string, spaceId?: string, isWall?: boolean): Promise<ApiResponse> {
  const formData = new FormData();
  formData.append('surface', surface);
  formData.append('mask_base64', maskBase64);
  if (spaceId) formData.append('space_id', spaceId);
  if (isWall !== undefined) formData.append('is_wall', String(isWall));

  const response = await api.post('/add_surface', formData);
  return response.data;
}

/**
 * Add a whole staircase from ONE region mask: the geometry service splits it
 * into treads and risers (stairs_0 = bottom step) and geometry is recomputed
 * once for all pieces.
 */
export async function addStairs(regionBase64: string, spaceId?: string): Promise<ApiResponse & { pieces?: number; risers?: number; treads?: number }> {
  const formData = new FormData();
  formData.append('region_base64', regionBase64);
  if (spaceId) formData.append('space_id', spaceId);

  const response = await api.post('/add_stairs', formData, { timeout: 300000 });
  return response.data;
}

/**
 * Add several walls, treads or risers at once, one mask each: geometry is
 * recomputed once for all of them. One unusable mask refuses the whole batch.
 */
export async function addSurfaces(surface: "walls" | "stairs", masksBase64: string[], spaceId?: string, isWall?: boolean): Promise<ApiResponse & { added?: number }> {
  const formData = new FormData();
  formData.append('surface', surface);
  masksBase64.forEach((m) => formData.append('masks', m));
  if (spaceId) formData.append('space_id', spaceId);
  if (isWall !== undefined) formData.append('is_wall', String(isWall));

  const response = await api.post('/add_surfaces', formData, { timeout: 300000 });
  return response.data;
}

// Geometry editor: texture frames drawn over the photo, applied exactly
export interface SurfaceFrame {
  kind: "floor" | "ceiling" | "walls" | "stairs";
  index: number;
  name: string;
  is_wall: boolean;
  plane: [number, number, number, number];
  vertical: [number, number, number];
  horizontal: [number, number, number];
  origin: [number, number, number];
  centroid: [number, number];
  texture_offset: [number, number];
  outline: [number, number][][];
}

export interface GeometryFrames extends ApiResponse {
  width: number;
  height: number;
  intrinsics: { fx: number; fy: number; cx: number; cy: number; estimated?: boolean };
  surfaces: SurfaceFrame[];
}

export async function getGeometryFrames(spaceId: string): Promise<GeometryFrames> {
  const response = await api.get('/geometry_frames', { params: { space_id: spaceId } });
  return response.data;
}

export async function setPlaneFrame(
  surface: string,
  index: number,
  frame: { plane: number[]; vertical: number[]; horizontal: number[]; origin: number[] },
  spaceId: string,
): Promise<ApiResponse> {
  const formData = new FormData();
  formData.append('surface', surface);
  formData.append('index_string', String(index));
  formData.append('plane', JSON.stringify(frame.plane));
  formData.append('vertical', JSON.stringify(frame.vertical));
  formData.append('horizontal', JSON.stringify(frame.horizontal));
  formData.append('origin', JSON.stringify(frame.origin));
  formData.append('space_id', spaceId);
  const response = await api.post('/set_plane_frame', formData);
  return response.data;
}

// Geometry operations
export async function recomputeGeometry(spaceId?: string): Promise<ApiResponse> {
  const formData = new FormData();
  if (spaceId) formData.append('space_id', spaceId);

  const response = await api.post('/recompute_geometry_full', formData, { timeout: 300000 });
  return response.data;
}

export async function editPlane(
  surface: string,
  index: number,
  a: number,
  b: number,
  c: number,
  d: number,
  vvx: number,
  vvy: number,
  vvz: number,
  spaceId?: string
): Promise<ApiResponse> {
  const response = await api.get('/edit_plane', {
    params: { surface, index_string: index.toString(), a, b, c, d, vvx, vvy, vvz, ...(spaceId ? { space_id: spaceId } : {}) },
  });
  return response.data;
}

export async function getPlaneCoefficients(
  surface: string,
  index: number
): Promise<ApiResponse<{ a: number; b: number; c: number; d: number; vvx: number; vvy: number; vvz: number }>> {
  const response = await api.get('/get_plane_coefficients', {
    params: { surface, index_string: index.toString() },
  });
  return response.data;
}

// Shading operations
export async function getShading(spaceId?: string): Promise<Blob> {
  const response = await api.get('/get_shading', {
    params: spaceId ? { space_id: spaceId } : {},
    responseType: 'blob',
  });
  return response.data;
}

export async function regenerateShading(
  spaceId?: string,
  blurEnabled = true,
  blurRadius = 2.0,
  removeThinStructures = true
): Promise<{ status: string; message: string; shading?: string }> {
  const formData = new FormData();
  if (spaceId) formData.append('space_id', spaceId);
  formData.append('blur_enabled', blurEnabled.toString());
  formData.append('blur_radius', blurRadius.toString());
  formData.append('remove_thin_structures', removeThinStructures.toString());

  const response = await api.post('/regenerate_shading', formData, { timeout: 300000 });
  return response.data;
}

export async function uploadShading(file: Blob, spaceId?: string): Promise<ApiResponse> {
  const formData = new FormData();
  formData.append('file', file, 'shading.png');
  if (spaceId) formData.append('space_id', spaceId);

  const response = await api.post('/upload_shading', formData, { timeout: 60000 });
  return response.data;
}

// Texture mapping
export async function applyTexture(
  textureBase64: string,
  surfaces: string[],
  scale: number,
  rotation: number,
  offsetX: number,
  offsetY: number,
  preserveShading: boolean
): Promise<MappingResult> {
  const formData = new FormData();
  formData.append('texture_base64', textureBase64);
  formData.append('surfaces', surfaces.join(','));
  formData.append('scale', scale.toString());
  formData.append('rotation', rotation.toString());
  formData.append('offset_x', offsetX.toString());
  formData.append('offset_y', offsetY.toString());
  formData.append('preserve_shading', preserveShading.toString());

  const response = await api.post('/apply_texture', formData, { timeout: 120000 });
  return response.data;
}

// 3D operations
export async function downloadMesh(path?: string): Promise<Blob> {
  const response = await api.get('/download_mesh', {
    params: path ? { path } : {},
    responseType: 'blob',
  });
  return response.data;
}

export interface PlaneCoefficients {
  a: number;
  b: number;
  c: number;
  d: number;
  vvx: number;
  vvy: number;
  vvz: number;
}

export interface PlaneMetadata {
  [surfaceKey: string]: number[]; // [a, b, c, d, vvx, vvy, vvz]
}

export async function getPlaneMetadata(spaceId?: string): Promise<{ status: string; output: PlaneMetadata; message?: string }> {
  const response = await api.get('/get_plane_metadata', {
    params: spaceId ? { space_id: spaceId } : {},
  });
  return response.data;
}

export async function getPlaneSingle(surface: string, index: number, spaceId?: string): Promise<{ status: string; output?: string; message?: string }> {
  const response = await api.get('/get_plane_single', {
    params: { surface, index_string: index.toString(), ...(spaceId ? { space_id: spaceId } : {}) },
  });
  return response.data;
}

export async function convertPlaneCoefs(
  coefficients: string,
  surface: string,
  index: number,
  reverse = false
): Promise<{ status: string; output?: string; message?: string }> {
  const response = await api.get('/convert_plane_coefs', {
    params: { coefficients, surface, index_string: index.toString(), reverse: reverse.toString() },
  });
  return response.data;
}

// Save operations
export async function saveSpace(): Promise<ApiResponse> {
  const response = await api.post('/save_space', {}, { timeout: 60000 });
  return response.data;
}

// Texture offset and edge alignment operations (for aligning textures between surfaces)
export type TextureEdgeH = 'none' | 'left' | 'right';
export type TextureEdgeV = 'none' | 'top' | 'bottom';

export interface TextureOffsetResponse {
  status: string;
  message: string;
  output?: {
    surface: string;
    index: number;
    texture_offset_h: number;
    texture_offset_v: number;
    texture_edge_h: TextureEdgeH;
    texture_edge_v: TextureEdgeV;
  };
}

export async function getTextureOffset(
  surface: string,
  index: number,
  spaceId?: string
): Promise<TextureOffsetResponse> {
  const response = await api.get('/get_texture_offset', {
    params: {
      surface,
      index_string: index.toString(),
      ...(spaceId ? { space_id: spaceId } : {}),
    },
  });
  return response.data;
}

export async function updateTextureOffset(
  surface: string,
  index: number,
  offsetH: number,
  offsetV: number,
  edgeH: TextureEdgeH = 'none',
  edgeV: TextureEdgeV = 'none',
  spaceId?: string
): Promise<TextureOffsetResponse> {
  const formData = new FormData();
  formData.append('surface', surface);
  formData.append('index_string', index.toString());
  formData.append('offset_h', offsetH.toString());
  formData.append('offset_v', offsetV.toString());
  formData.append('edge_h', edgeH);
  formData.append('edge_v', edgeV);
  if (spaceId) formData.append('space_id', spaceId);

  const response = await api.post('/update_texture_offset', formData);
  return response.data;
}

// Vendor/Product operations
export interface Vendor {
  id: string;
  name: string;
  companyName?: string;
  alias?: string;
}

export interface Product {
  id: string;
  name: string;
  thumbnailPath?: string;
  tilePath?: string;
  materialPath?: string;
  width?: number;
  height?: number;
  category?: string;
  brand?: string;
  type?: string;
  sizeName?: string;
}

// PMS API for product search
const PMS_API_URL = process.env.REACT_APP_PMS_API_URL || 'https://pim-stg.ardealy.com';

function getPmsAuthHeaders(): Record<string, string> {
  const token = AuthClientStore.getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface SearchProductsParams {
  page?: number;
  limit?: number;
  name?: string;
  application_types?: string[];
}

export interface SearchProductsResponse {
  data: Product[];
  total: number;
  page: number;
  limit: number;
  offset: number;
}

export async function searchProducts(params: SearchProductsParams = {}): Promise<SearchProductsResponse> {
  const response = await axios.post(
    `${PMS_API_URL}/services/pms/products/search`,
    {
      page: params.page ?? 1,
      limit: params.limit ?? 24,
      ...params,
    },
    { headers: { 'Content-Type': 'application/json', ...getPmsAuthHeaders() } }
  );
  return response.data;
}

export async function getVendors(): Promise<{ status: string; vendors: Vendor[]; message?: string }> {
  const response = await api.get('/vendors');
  return response.data;
}

export async function getProductsByVendor(vendorId: string, limit = 50): Promise<{ status: string; products: Product[]; message?: string }> {
  const response = await api.get(`/products/${vendorId}`, { params: { limit } });
  return response.data;
}

export async function getProductTexture(productId: string, setAsTile = false, spaceId?: string): Promise<Blob> {
  const response = await api.get(`/product/${productId}/texture`, {
    params: { set_as_tile: setAsTile, ...(spaceId ? { space_id: spaceId } : {}) },
    responseType: 'blob',
  });
  return response.data;
}

// Run mapping for multiple surfaces
export async function runMapping(surfaces: string[], sideMax?: number, spaceId?: string, rotation?: number, productId?: string): Promise<{ status: string; message: string; output?: string }> {
  const response = await api.post('/run_mapping_multi', {
    surfaces,
    side_max: sideMax ? sideMax.toString() : '',
    space_id: spaceId || '',
    orientation: rotation || 0,
    // Lets whichever server answers load the texture itself
    product_id: productId || '',
  }, { timeout: 120000 });
  return response.data;
}

// Update space
export async function updateSpace(
  visitorId: string,
  spaceName: string,
  associatedVendor: string,
  isPublic: boolean,
  spaceId?: string
): Promise<ApiResponse & { urls?: Record<string, string>; updated_files?: string[]; cache_cleared?: boolean }> {
  const formData = new FormData();
  formData.append('visitor_id', visitorId);
  formData.append('space_name', spaceName);
  formData.append('associated_vendor', associatedVendor);
  formData.append('is_public', isPublic.toString());
  // Replica-safe update: lets the server hydrate the session from the DB if
  // the request lands on a replica that never saw /load_space.
  if (spaceId) formData.append('space_id', spaceId);

  const response = await api.post('/update_space', formData, { timeout: 300000 });
  return response.data;
}

export default api;
